import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import posthog from 'posthog-js';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo } from 'react';

import { isInternalEmail } from '@/analytics/internalEmail';
import { useAuth } from '@/contexts/AuthContext';
import { useBackofficeUserQuery } from '@/hooks/queries/useBackofficeAuthQuery';
import { usePersistedState } from '@/hooks/usePersistedState';
import { queryKeys } from '@/lib/queryKeys';
import { supabase } from '@/lib/supabase';
import type { BackofficeUserWithDomains } from '@/services/backofficeAuth';

interface BackofficeAuthContextValue {
    /** The backoffice user profile, null if not authorized */
    backofficeUser: BackofficeUserWithDomains | null;
    /**
     * Effective staff access used for UI gating. False when a super-admin has
     * turned on "view as non-admin" preview, so staff/advanced sections hide.
     */
    isStaff: boolean;
    /** Real staff access, ignoring the "view as non-admin" preview override. */
    isRealStaff: boolean;
    /** Whether a super-admin is previewing the backoffice as a non-admin user. */
    viewAsNonAdmin: boolean;
    /** Toggle the "view as non-admin" preview (super-admin only; persisted). */
    setViewAsNonAdmin: (value: boolean) => void;
    /**
     * Per-user "Advanced mode" (IX-5374): reveals `x-tier: advanced` settings.
     * Stored in Supabase auth `user_metadata`, so it follows the user across
     * devices and workspaces. Visibility only: `user_metadata` is user-writable,
     * never gate access on it.
     */
    showAdvanced: boolean;
    /** Persist Advanced mode; `onAuthStateChange` (USER_UPDATED) republishes the user. */
    setShowAdvanced: (value: boolean) => Promise<void>;
    /** Whether the user is a client account admin of at least one account (IX-3872) */
    isAccountAdmin: boolean;
    /** Domains of the accounts the user is an account admin of (IX-4441) */
    adminDomains: string[];
    /** List of domains the user can access */
    accessibleDomains: string[];
    /** Whether the initial check is still loading */
    loading: boolean;
    /** Error message if something went wrong */
    error: string | null;
    /**
     * True only when the profile query failed with no prior successful result
     * (React Query `status === 'error'`, i.e. `data` never resolved). A refetch
     * that fails while cached data exists — including the `null` a real prospect
     * gets — stays `success`, so this is the safe signal to gate a hard error
     * screen on without interrupting onboarding.
     */
    hasUnresolvedProfileError: boolean;
    /** Refetch the backoffice user profile */
    refetch: () => Promise<void>;
}

/** `user_metadata` key holding the per-user Advanced mode preference. */
const ADVANCED_MODE_KEY = 'advanced_mode';

async function setShowAdvanced(value: boolean): Promise<void> {
    const { error } = await supabase.auth.updateUser({ data: { [ADVANCED_MODE_KEY]: value } });
    if (error) throw error;
}

const BackofficeAuthContext = createContext<BackofficeAuthContextValue | null>(null);

export function BackofficeAuthProvider({ children }: { children: ReactNode }) {
    const { user, loading: authLoading } = useAuth();
    const userId = user?.id ?? null;
    const queryClient = useQueryClient();

    const {
        data: backofficeUser,
        isLoading: queryLoading,
        isError: queryIsError,
        error: queryError,
    } = useBackofficeUserQuery(userId);

    const [viewAsNonAdmin, setViewAsNonAdmin] = usePersistedState('view-as-non-admin', false);
    const isRealStaff = backofficeUser?.is_admin ?? false;
    const showAdvanced = user?.user_metadata?.[ADVANCED_MODE_KEY] === true;

    // Identify user in PostHog and Sentry when backoffice profile is loaded
    useEffect(() => {
        if (backofficeUser && user) {
            const isStaff = backofficeUser.is_admin;

            const emailDomain = backofficeUser.email.split('@')[1] || '';
            const environment = import.meta.env.VITE_ENVIRONMENT || import.meta.env.MODE;
            const appVersion = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown';

            posthog.identify(user.id, {
                email: backofficeUser.email,
                name: backofficeUser.display_name || user.user_metadata?.full_name,
                is_staff: isStaff,
                email_domain: emailDomain,
                environment,
                app_version: appVersion,
            });

            Sentry.setUser({
                id: user.id,
                email: backofficeUser.email,
                username: backofficeUser.display_name || undefined,
            });
            Sentry.setTag('is_staff', isStaff);
            Sentry.setTag('email_domain', emailDomain);
            Sentry.setTag('app_version', appVersion);
        } else if (!user && posthog._isIdentified()) {
            // Only clear identity on a real logout — a previously identified user
            // signed out. Do NOT reset for a not-yet-claimed wizard user (auth
            // user present, backofficeUser still null): resetting there rotates
            // the anonymous id on every reload, orphaning the wizard funnel
            // events that identify(user.id) would otherwise merge (IX-3897).
            posthog.reset();
            Sentry.setUser(null);
        }
    }, [backofficeUser, user]);

    // Session replay for the pre-claim onboarding wizard, which renders outside
    // AppLayout (no workspace config here). Start for an external prospect who is
    // authenticated but not yet provisioned; stop on logout. Recording is NOT
    // stopped on provisioning — once claimed, the AppLayout gate
    // (OnboardingSessionReplay) takes over and keeps it running until the widget
    // goes live. Recording is disabled at init (main.tsx), so startSessionRecording()
    // is what actually turns it on (it overrides disable_session_recording); the
    // PostHog project must also have Session Replay enabled.
    useEffect(() => {
        if (!user) {
            // Logout: a prospect who abandoned the wizard without going live.
            if (posthog.sessionRecordingStarted()) posthog.stopSessionRecording();
            return;
        }
        // Wait for the profile query to settle, and fail closed on its error: an
        // undefined `backofficeUser` from a failed query is not proof of a new
        // prospect (it could be an established client), so never start there.
        // Provisioned users are handed to the AppLayout gate — this effect only
        // owns the pre-claim wizard.
        if (queryLoading || queryError || backofficeUser) return;
        if (!isInternalEmail(user.email) && !posthog.sessionRecordingStarted()) {
            posthog.startSessionRecording();
        }
    }, [user, backofficeUser, queryLoading, queryError]);

    const refetch = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: queryKeys.backofficeAuth.user(userId) });
    }, [queryClient, userId]);

    const value = useMemo(
        () => ({
            backofficeUser: backofficeUser ?? null,
            isStaff: isRealStaff && !viewAsNonAdmin,
            isRealStaff,
            viewAsNonAdmin,
            setViewAsNonAdmin,
            showAdvanced,
            setShowAdvanced,
            isAccountAdmin: backofficeUser?.is_account_admin ?? false,
            adminDomains: backofficeUser?.admin_domains ?? [],
            accessibleDomains: backofficeUser?.accessible_domains ?? [],
            loading: authLoading || queryLoading,
            error: queryError
                ? queryError instanceof Error
                    ? queryError.message
                    : 'Failed to fetch user permissions'
                : null,
            hasUnresolvedProfileError: queryIsError,
            refetch,
        }),
        [
            backofficeUser,
            isRealStaff,
            viewAsNonAdmin,
            setViewAsNonAdmin,
            showAdvanced,
            authLoading,
            queryLoading,
            queryIsError,
            queryError,
            refetch,
        ]
    );

    return (
        <BackofficeAuthContext.Provider value={value}>{children}</BackofficeAuthContext.Provider>
    );
}

export function useBackofficeAuth(): BackofficeAuthContextValue {
    const context = useContext(BackofficeAuthContext);
    if (!context) {
        throw new Error('useBackofficeAuth must be used within a BackofficeAuthProvider');
    }
    return context;
}
